import { defineStore } from 'pinia'
import { TemplateBackgroundTypeEnum, TemplateTypeEnum, OrientationEnum } from 'src/common/enums'
import type {
  ITemplateLayer,
  ITemplateLayerItem,
  ITemplateFieldGroups,
  TemplateContentType,
  TemplateSizeType,
  ITemplate,
  TemplateItemLayerValue,
} from 'src/common/interfaces'

interface IActiveLayer {
  index: number
  id?: number | string
  updatedFrom?: 'section' | null
}

type Background = string | { blob: string; file: File }

export type BaseTemplateAlignDirection = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom'

// Column layout grid of the editor canvas (like Figma's), all sizes in canvas px. Editor-only, not saved.
export interface ILayoutGrid {
  isVisible: boolean
  columns: number
  gutter: number
  margin: number
}

interface IState {
  templateName: string
  templateType: TemplateTypeEnum
  orientation: OrientationEnum
  backgroundType: TemplateBackgroundTypeEnum
  background: Background
  colours: string[]
  layers: ITemplateLayer[]
  uploadedImageIds: number[]
  newUploadImages: Record<string, { file?: File; blob?: string }>
  activeLayer: IActiveLayer
  activeItem?: ITemplateLayerItem | Record<string, unknown>
  templateSize: TemplateSizeType
  defaultContentTypes: Record<TemplateContentType, ITemplateLayerItem>
  templateFields: ITemplateFieldGroups | Record<string, never>
  selectedItemIds: number[]
  isMultiSelectMode: boolean
  isDragging: boolean
  history: ITemplateLayer[][]
  historyIndex: number
  layoutGrid: ILayoutGrid
}

const MAX_HISTORY = 50

let pendingHistoryPush = false

// Smallest size a resize can give an item. A divider's height is its line thickness, so it can go down to 1px.
export function getItemMinSize(item: ITemplateLayerItem) {
  return { width: 20, height: item.type === 'shape' && item.value === 'line' ? 1 : 20 }
}

function getItemVisualBounds(item: ITemplateLayerItem) {
  const width = item.width
  const height = item.height
  const centerX = item.start_position.x + width / 2
  const centerY = item.start_position.y + height / 2
  if (!item.rotate) {
    return { x: item.start_position.x, y: item.start_position.y, width, height, centerX, centerY }
  }
  const radians = (item.rotate * Math.PI) / 180
  const visualWidth = Math.abs(width * Math.cos(radians)) + Math.abs(height * Math.sin(radians))
  const visualHeight = Math.abs(width * Math.sin(radians)) + Math.abs(height * Math.cos(radians))
  return {
    x: centerX - visualWidth / 2,
    y: centerY - visualHeight / 2,
    width: visualWidth,
    height: visualHeight,
    centerX,
    centerY,
  }
}

export const useTemplateStore = defineStore('template', {
  state: (): IState => ({
    templateName: '',
    templateType: TemplateTypeEnum.RECEIPT,
    orientation: OrientationEnum.VERTICAL,
    backgroundType: TemplateBackgroundTypeEnum.COLOR,
    background: '',
    colours: [],

    layers: [
      {
        id: 'receipt___header',
        title: 'layers.receipt_header',
        color: '#74a8f5',
        height: 450,
        items: [],
      },
      {
        id: 'receipt___column_titles',
        title: 'layers.receipt_column_titles',
        color: '#BAEC69',
        height: 230,
        items: [],
      },
      {
        id: 'receipt___body',
        title: 'layers.receipt_body',
        color: '#FFB78F',
        height: 350,
        items: [],
      },
      {
        id: 'receipt__payment_summary',
        title: 'layers.payment_summary',
        color: '#F398DF',
        height: 150,
        items: [],
      },
      {
        id: 'receipt___footer',
        title: 'layers.footer',
        color: '#AB9AEF',
        height: 200,
        items: [],
      },
    ],

    defaultContentTypes: {
      text: {
        type: 'text',
        value: 'Add Text',
        start_position: { x: 0, y: 0 },
        rotate: 0,
        width: 100,
        height: 32,
        font_size: 14,
        font_weight: 500,
        text_align: 'left',
        font_family: 'Poppins',
        colour: '#000000',
        is_show: true,
        is_field: false,
        id: 0,
      },
      image: {
        type: 'image',
        value: '',
        start_position: { x: 0, y: 0 },
        radius: 0,
        width: 70,
        height: 70,
        rotate: 0,
        is_show: true,
        is_field: false,
        id: 0,
      },
      shape: {
        type: 'shape',
        value: 'line',
        variant: 'solid',
        start_position: { x: 0, y: 0 },
        rotate: 0,
        width: 70,
        height: 70,
        colour: '#000000',
        is_show: true,
        is_field: false,
        id: 0,
      },
    },
    templateFields: {},

    uploadedImageIds: [],

    newUploadImages: {},

    activeLayer: { index: -1 },

    activeItem: {},

    templateSize: 'a4',

    selectedItemIds: [],
    isMultiSelectMode: false,
    isDragging: false,
    history: [],
    historyIndex: -1,
    layoutGrid: { isVisible: false, columns: 6, gutter: 16, margin: 16 },
  }),
  actions: {
    handleSetLayers(layers: ITemplateLayer[]) {
      this.layers = layers.map((layer) => ({
        ...layer,
        items: layer.items.map((item) => ({
          ...item,
          start_position: item.start_position ?? { x: 0, y: 0 },
        })),
      }))
      this.initHistory()
    },
    handleSetUploadedImageIds(imagesId: number[]) {
      this.uploadedImageIds = imagesId
    },

    // this is for left section sidebar
    handleToggleShow(layerIndex: number, itemIndex: number) {
      const item = this.layers[layerIndex].items[itemIndex]
      item.is_show = !item.is_show
      if (!item.is_show && item.id === this.activeItem?.id) {
        this.handleActiveItem({})
      } else if (item.is_show) this.handleActiveItem(item)
    },
    // this is for left section sidebar
    handleRowDragged({ newItems, layerIndex }: { newItems: ITemplateLayerItem[]; layerIndex: number }) {
      this.layers[layerIndex].items = newItems
      this._pushHistory()
    },
    handleResizeLayer({ layerIndex, height }: Record<string, number>) {
      this.layers[layerIndex].height = height
      this._pushHistory()
    },

    handleAddContent({
      type,
      overrideFields = {},
    }: {
      type: TemplateContentType
      overrideFields: Partial<ITemplateLayerItem>
    }) {
      const { index: activeLayerIndex } = this.activeLayer
      if (activeLayerIndex !== -1) {
        const maxStartPositionX = this.isPrintTemplate ? 200 : 500
        const y =
          this.layers[activeLayerIndex].height - 20 > 0
            ? Math.floor(Math.random() * (this.layers[activeLayerIndex].height - 20))
            : 0
        const x = Math.floor(Math.random() * maxStartPositionX)

        this.layers[activeLayerIndex].items.unshift({
          ...this.defaultContentTypes[type],
          ...overrideFields,
          start_position: { y, x },
          id: type !== 'image' ? Date.now() : this.getLastUploadedImageId,
        })
        // Select what was just added, like the table map editor does; a selected text also fits its height
        this.handleActiveItem(this.layers[activeLayerIndex].items[0])
        this._pushHistory()
      }
    },
    handleActiveLayer(payload: IActiveLayer) {
      this.activeLayer = payload
    },
    handleActiveItem(item: ITemplateLayerItem | Record<string, never>) {
      this.activeItem = item
      const id = (item as Partial<ITemplateLayerItem>).id
      this.selectedItemIds = id ? [id] : []
    },

    selectItem(id: number | null) {
      if (id === null) {
        this.activeItem = {}
        this.selectedItemIds = []
        return
      }
      const item = this.layers.flatMap((layer) => layer.items).find((item) => item.id === id)
      if (item) {
        this.activeItem = item
        this.selectedItemIds = [id]
      }
    },

    toggleItemSelection(id: number) {
      const selectionIndex = this.selectedItemIds.indexOf(id)
      if (selectionIndex === -1) {
        this.selectedItemIds = [...this.selectedItemIds, id]
        if (this.selectedItemIds.length === 1) {
          const item = this.layers.flatMap((layer) => layer.items).find((item) => item.id === id)
          if (item) this.activeItem = item
        }
      } else {
        this.selectedItemIds = this.selectedItemIds.filter((existingId) => existingId !== id)
        if (this.selectedItemIds.length === 0) {
          this.activeItem = {}
        } else if ((this.activeItem as Partial<ITemplateLayerItem>).id === id) {
          const firstId = this.selectedItemIds[0]
          const item = this.layers.flatMap((layer) => layer.items).find((item) => item.id === firstId)
          this.activeItem = item ?? {}
        }
      }
    },

    alignItems(direction: BaseTemplateAlignDirection) {
      const items = this.selectedItems
      if (items.length < 2) return
      const bounds = items.map((item) => getItemVisualBounds(item))
      const minX = Math.min(...bounds.map((b) => b.x))
      const maxRight = Math.max(...bounds.map((b) => b.x + b.width))
      const minY = Math.min(...bounds.map((b) => b.y))
      const maxBottom = Math.max(...bounds.map((b) => b.y + b.height))
      const centerX = (minX + maxRight) / 2
      const centerY = (minY + maxBottom) / 2
      items.forEach((item, index) => {
        const visualBounds = bounds[index]
        if (direction === 'left') item.start_position.x = minX + visualBounds.width / 2 - item.width / 2
        else if (direction === 'center-h') item.start_position.x = Math.round(centerX - item.width / 2)
        else if (direction === 'right') item.start_position.x = maxRight - visualBounds.width / 2 - item.width / 2
        else if (direction === 'top') item.start_position.y = minY + visualBounds.height / 2 - item.height / 2
        else if (direction === 'center-v') item.start_position.y = Math.round(centerY - item.height / 2)
        else if (direction === 'bottom') item.start_position.y = maxBottom - visualBounds.height / 2 - item.height / 2
      })
      this._pushHistory()
    },

    handleItemPositionValidating({
      layerIndex,
      itemIndex,
      axes = ['x', 'y'],
    }: {
      layerIndex: number
      itemIndex: number
      axes?: Array<'x' | 'y'>
    }) {
      const layer = this.layers[layerIndex]
      const item = layer.items[itemIndex]

      if (!item) return

      if (axes.includes('x')) {
        if (item.start_position.x < 0) item.start_position.x = 0
        if (item.start_position.x + item.width > this.templateWidth)
          item.start_position.x = this.templateWidth - item.width
      }
      if (axes.includes('y')) {
        if (item.start_position.y < 0) item.start_position.y = 0
        if (item.start_position.y + item.height > layer.height) item.start_position.y = layer.height - item.height
      }
    },

    handleUpdateItemSetting(field: keyof ITemplateLayerItem, value: TemplateItemLayerValue) {
      const layer = this.layers[this.activeLayer.index]
      const itemIndex = layer.items.findIndex((layerItem) => layerItem.id === this.activeItem?.id)

      if (itemIndex === -1) return { itemIndex }

      layer.items[itemIndex][field] = value as never
      this._pushHistory()
      return { itemIndex }
    },
    handleDeleteItem({ layerIndex, itemIndex }: Record<string, number>) {
      const item = this.layers[layerIndex].items[itemIndex]

      if (item.type === 'image') {
        const uploadIndex = this.uploadedImageIds.findIndex((uploadId) => uploadId === item.id)
        if (uploadIndex !== -1) {
          this.uploadedImageIds.splice(uploadIndex, 1)
          delete this.newUploadImages[item.id]
        }
      }

      this.layers[layerIndex].items.splice(itemIndex, 1)
      this.activeItem = {}
      this.selectedItemIds = []
      this._pushHistory()
    },

    handleSetNewImage({ id, blob, file }: { id: number | string; blob: string; file: File }) {
      this.newUploadImages[id] = { blob, file }
    },

    handleSetBackground(background: Background) {
      this.background = background
    },

    // this action use for clear newUploadImages state after user upload template
    handleResetNewImages() {
      this.newUploadImages = {}
    },
    handleSetTemplateSize(size: TemplateSizeType) {
      this.templateSize = size
    },
    handleSetDigitalMenuKeys(template: ITemplate) {
      this.background = template.background
      this.colours = template.colours
      this.backgroundType = template.background_type
      this.orientation = template.orientation
      this.templateType = template.template_type
    },
    handleSetTemplateFields(fields: ITemplateFieldGroups) {
      this.templateFields = fields
    },
    handleSetTemplateName(name: string) {
      this.templateName = name
    },

    initDigitalMenu() {
      this.templateType = TemplateTypeEnum.DIGITAL_MENU
      this.layers = [
        {
          id: 'receipt___header',
          title: 'layers.receipt_header',
          color: '#74a8f5',
          height: 450,
          items: [],
        },
      ]
    },

    initHistory() {
      this.history = [JSON.parse(JSON.stringify(this.layers))]
      this.historyIndex = 0
    },

    undo() {
      if (!this.canUndo) return
      this.historyIndex--
      this.layers = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.activeItem = {}
      this.selectedItemIds = []
    },

    redo() {
      if (!this.canRedo) return
      this.historyIndex++
      this.layers = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.activeItem = {}
      this.selectedItemIds = []
    },

    moveSelectedItemsTo(positions: Record<number, { x: number; y: number }>) {
      for (const layer of this.layers) {
        for (const item of layer.items) {
          const pos = positions[item.id]
          if (pos === undefined) continue
          let { x, y } = pos
          if (x < 0) x = 0
          if (y < 0) y = 0
          if (x + item.width > this.templateWidth) x = this.templateWidth - item.width
          if (y + item.height > layer.height) y = layer.height - item.height
          item.start_position.x = x
          item.start_position.y = y
        }
      }
    },

    resizeItem({ id, x, y, width, height }: { id: number; x: number; y: number; width: number; height: number }) {
      for (let layerIndex = 0; layerIndex < this.layers.length; layerIndex++) {
        const layer = this.layers[layerIndex]
        const item = layer.items.find((layerItem) => layerItem.id === id)
        if (!item) continue
        const { width: minWidth, height: minHeight } = getItemMinSize(item)
        let newWidth = Math.max(minWidth, width)
        let newHeight = Math.max(minHeight, height)
        let newX = x
        let newY = y
        // x/y/width/height describe the item before rotation. Only at 0° or 180° is that box what's on screen,
        // so only then can it be clamped to the section without moving the corner the resize keeps fixed.
        const isAxisAligned = (((item.rotate ?? 0) % 180) + 180) % 180 === 0
        if (isAxisAligned) {
          if (newX < 0) {
            newWidth += newX
            newX = 0
          }
          if (newY < 0) {
            newHeight += newY
            newY = 0
          }
          if (newX + newWidth > this.templateWidth) newWidth = this.templateWidth - newX
          if (newY + newHeight > layer.height) newHeight = layer.height - newY
        }
        item.start_position.x = newX
        item.start_position.y = newY
        item.width = Math.max(minWidth, newWidth)
        // A text's height follows its wrapped lines (BaseTemplateContentText), so a resize only sets its width
        if (item.type !== 'text') item.height = Math.max(minHeight, newHeight)
        return
      }
    },

    nudgeSelectedItems(deltaX: number, deltaY: number) {
      const ids = new Set(this.selectedItemIds)
      if (!ids.size) return
      for (const layer of this.layers) {
        for (const item of layer.items) {
          if (!ids.has(item.id)) continue
          let x = item.start_position.x + deltaX
          let y = item.start_position.y + deltaY
          if (x < 0) x = 0
          if (y < 0) y = 0
          if (x + item.width > this.templateWidth) x = this.templateWidth - item.width
          if (y + item.height > layer.height) y = layer.height - item.height
          item.start_position.x = x
          item.start_position.y = y
        }
      }
      this._pushHistory()
    },

    rotateSelectedItems(angle: number) {
      const items = this.selectedItems
      if (!items.length) return

      if (items.length === 1) {
        items[0].rotate = angle
        this._pushHistory()
        return
      }

      // Measure pre-rotation visual gaps along both axes
      const sortedByX = [...items].sort((a, b) => getItemVisualBounds(a).x - getItemVisualBounds(b).x)
      let totalHorizontalGap = 0
      for (let index = 1; index < sortedByX.length; index++) {
        const prevBounds = getItemVisualBounds(sortedByX[index - 1])
        const nextBounds = getItemVisualBounds(sortedByX[index])
        totalHorizontalGap += nextBounds.x - (prevBounds.x + prevBounds.width)
      }
      const avgHorizontalGap = totalHorizontalGap / (sortedByX.length - 1)

      const sortedByY = [...items].sort((a, b) => getItemVisualBounds(a).y - getItemVisualBounds(b).y)
      let totalVerticalGap = 0
      for (let index = 1; index < sortedByY.length; index++) {
        const prevBounds = getItemVisualBounds(sortedByY[index - 1])
        const nextBounds = getItemVisualBounds(sortedByY[index])
        totalVerticalGap += nextBounds.y - (prevBounds.y + prevBounds.height)
      }
      const avgVerticalGap = totalVerticalGap / (sortedByY.length - 1)

      // Apply rotation to all items
      items.forEach((item) => {
        item.rotate = angle
      })

      // Redistribute horizontally using new visual bounds to preserve gap
      if (avgHorizontalGap >= 0) {
        sortedByX.sort((a, b) => getItemVisualBounds(a).x - getItemVisualBounds(b).x)
        const firstHorizontalBounds = getItemVisualBounds(sortedByX[0])
        let cursor = firstHorizontalBounds.x + firstHorizontalBounds.width + avgHorizontalGap
        for (let index = 1; index < sortedByX.length; index++) {
          const visualBounds = getItemVisualBounds(sortedByX[index])
          sortedByX[index].start_position.x = cursor + visualBounds.width / 2 - sortedByX[index].width / 2
          cursor += visualBounds.width + avgHorizontalGap
        }
      }

      // Redistribute vertically using new visual bounds to preserve gap
      if (avgVerticalGap >= 0) {
        sortedByY.sort((a, b) => getItemVisualBounds(a).y - getItemVisualBounds(b).y)
        const firstVerticalBounds = getItemVisualBounds(sortedByY[0])
        let cursor = firstVerticalBounds.y + firstVerticalBounds.height + avgVerticalGap
        for (let index = 1; index < sortedByY.length; index++) {
          const visualBounds = getItemVisualBounds(sortedByY[index])
          sortedByY[index].start_position.y = cursor + visualBounds.height / 2 - sortedByY[index].height / 2
          cursor += visualBounds.height + avgVerticalGap
        }
      }

      this._pushHistory()
    },

    distributeWithGap(gap: number, direction: 'horizontal' | 'vertical') {
      const items = this.selectedItems
      if (items.length < 2) return
      // Compensates for item box padding so the visual gap matches the user-supplied value
      const ITEM_BOX_PADDING = 6
      const adjustedGap = gap + ITEM_BOX_PADDING
      if (direction === 'horizontal') {
        const sorted = [...items].sort((a, b) => getItemVisualBounds(a).x - getItemVisualBounds(b).x)
        const firstBounds = getItemVisualBounds(sorted[0])
        let cursor = firstBounds.x + firstBounds.width + adjustedGap
        for (let index = 1; index < sorted.length; index++) {
          const visualBounds = getItemVisualBounds(sorted[index])
          sorted[index].start_position.x = cursor + visualBounds.width / 2 - sorted[index].width / 2
          cursor += visualBounds.width + adjustedGap
        }
      } else {
        const sorted = [...items].sort((a, b) => getItemVisualBounds(a).y - getItemVisualBounds(b).y)
        const firstBounds = getItemVisualBounds(sorted[0])
        let cursor = firstBounds.y + firstBounds.height + adjustedGap
        for (let index = 1; index < sorted.length; index++) {
          const visualBounds = getItemVisualBounds(sorted[index])
          sorted[index].start_position.y = cursor + visualBounds.height / 2 - sorted[index].height / 2
          cursor += visualBounds.height + adjustedGap
        }
      }
      this._pushHistory()
    },

    duplicateSelectedItems() {
      const ids = new Set(this.selectedItemIds)
      const newIds: number[] = []
      let idCounter = Date.now()
      for (const layer of this.layers) {
        const copies: ITemplateLayerItem[] = []
        for (const item of layer.items) {
          if (!ids.has(item.id)) continue
          const copy: ITemplateLayerItem = {
            ...JSON.parse(JSON.stringify(item)),
            id: idCounter++,
            start_position: { x: item.start_position.x + 10, y: item.start_position.y + 10 },
          }
          copies.push(copy)
          newIds.push(copy.id)
        }
        layer.items.unshift(...copies)
      }
      this.activeItem = {}
      this.selectedItemIds = [...newIds]
      if (newIds.length === 1) {
        const found = this.layers.flatMap((layer) => layer.items).find((item) => item.id === newIds[0])
        if (found) this.activeItem = found
      }
      this._pushHistory()
    },

    deleteSelectedItems() {
      const ids = new Set(this.selectedItemIds)
      for (const layer of this.layers) {
        layer.items = layer.items.filter((item) => {
          if (!ids.has(item.id)) return true
          if (item.type === 'image') {
            const uploadIndex = this.uploadedImageIds.findIndex((uploadId) => uploadId === item.id)
            if (uploadIndex !== -1) {
              this.uploadedImageIds.splice(uploadIndex, 1)
              delete this.newUploadImages[item.id]
            }
          }
          return false
        })
      }
      this.activeItem = {}
      this.selectedItemIds = []
      this._pushHistory()
    },

    setLayoutGrid(field: 'columns' | 'gutter' | 'margin', value: number | string) {
      const minimum = field === 'columns' ? 1 : 0
      this.layoutGrid[field] = Math.max(minimum, Math.round(Number(value) || 0))
    },

    _pushHistory() {
      if (this.isDragging) return
      if (pendingHistoryPush) return
      pendingHistoryPush = true

      Promise.resolve().then(() => {
        if (this.isDragging) {
          pendingHistoryPush = false
          return
        }
        const snapshot = JSON.parse(JSON.stringify(this.layers))
        this.history = this.history.slice(0, this.historyIndex + 1)
        this.history.push(snapshot)
        if (this.history.length > MAX_HISTORY) {
          this.history.shift()
        } else {
          this.historyIndex++
        }
        pendingHistoryPush = false
      })
    },
  },
  getters: {
    getLastUploadedImageId(): number {
      return this.uploadedImageIds[this.uploadedImageIds.length - 1] || 0
    },
    isPrintTemplate(): boolean {
      return this.templateSize === 'receipt'
    },
    isDigitalMenu(): boolean {
      return this.templateType === TemplateTypeEnum.DIGITAL_MENU
    },

    isVertical(): boolean {
      return this.orientation === OrientationEnum.VERTICAL
    },

    templateWidth(): number {
      return this.isDigitalMenu ? (this.isVertical ? 450 : 800) : this.isPrintTemplate ? 390 : 720
    },

    canUndo(): boolean {
      return this.historyIndex > 0
    },

    canRedo(): boolean {
      return this.historyIndex < this.history.length - 1
    },

    isMultiSelect(): boolean {
      return this.selectedItemIds.length > 1
    },

    // Left and right edge of each layout grid column; empty when the columns don't fit the template width
    layoutGridColumns(): Array<{ left: number; right: number }> {
      const { columns, gutter, margin } = this.layoutGrid
      const columnWidth = (this.templateWidth - margin * 2 - gutter * (columns - 1)) / columns
      if (columnWidth <= 0) return []
      return Array.from({ length: columns }, (_, index) => {
        const left = margin + index * (columnWidth + gutter)
        return { left, right: left + columnWidth }
      })
    },

    selectedItems(): ITemplateLayerItem[] {
      const ids = new Set(this.selectedItemIds)
      return this.layers.flatMap((layer) => layer.items).filter((item) => ids.has(item.id))
    },
  },
})
